import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";

import {
  TestQuestionData,
  TestQuestionFrontType,
  ValidationError,
} from "../../types";
import {
  DEFAULT_RESULT_VALUE,
  MULTISELECT_ENABLED,
  WAIT_USER_ANSWER_DATA,
} from "./constants";
import { useTimer } from "./hook";
import {
  ContinueQuestion,
  DEFAULT_CURRENT_QUESTION,
  INITIAL_TEST_SOLUTION_STATE,
  SaveAnswerHandler,
  TestSolutionAction,
  TestSolutionActionsContext,
  TestSolutionActionType,
  TestSolutionState,
  TestSolutionValuesContext,
} from "./test-solution-context";
import {
  getAnswers,
  getDefaultUserAnswer,
  getFormattedUserAnswer,
  getIsSendRequest,
  getQuestionType,
  parseServerUserAnswer,
} from "./utils";

const NULL_ANSWER_MARKER = "<nil>";

type Props = {
  children: ReactNode,
};

const getComputedNextDisabled = (state: TestSolutionState) => {
  const {
    userAnswer,
    isSaveAnswerLoading,
    questionType,
    isFileUpload,
    fileAnswer,
    error,
    answers,
    currentQuestion,
  } = state;

  const defaultDisabled =
    userAnswer === null ||
    userAnswer === "" ||
    userAnswer === WAIT_USER_ANSWER_DATA ||
    isSaveAnswerLoading ||
    (isFileUpload && fileAnswer === "" && questionType === TestQuestionFrontType.Open) ||
    error.hasError;

  let result = defaultDisabled;

  switch (questionType) {
    case TestQuestionFrontType.ClosedMany:
    case TestQuestionFrontType.Survey:
    case TestQuestionFrontType.ClosedOne: {
      break;
    }
    case TestQuestionFrontType.Sequence: {
      result =
        Array.isArray(userAnswer) && userAnswer.length !== answers?.length;
      break;
    }
    case TestQuestionFrontType.Matching: {
      result =
        Array.isArray(userAnswer) &&
        (userAnswer.includes(null) ||
          userAnswer.length !==
          currentQuestion?.leftAnswers?.split("|")?.length);
      break;
    }
  }

  return result || defaultDisabled;
};

const reducer = (state: TestSolutionState,
  action: TestSolutionAction): TestSolutionState => {
  switch (action.type) {
    case TestSolutionActionType.QuestionLoadedFromServer: {
      const question = action.payload;

      if (!question) {
        return {
          ...state,
          currentQuestion: DEFAULT_CURRENT_QUESTION,
          questionType: undefined,
          answers: [],
          isFileUpload: false,
        };
      }

      const isMultiselect = question?.multiselect === MULTISELECT_ENABLED;
      const questionType = getQuestionType(question?.questionType,
        isMultiselect) as TestQuestionFrontType;
      const answers = getAnswers(questionType, question);

      if (state.userAnswer !== WAIT_USER_ANSWER_DATA) {
        return {
          ...state,
          currentQuestion: question,
          questionType,
          answers,
          isFileUpload: question?.uploadFile === "checked",
        };
      }

      const serverUserAnswer = question?.userAnswer;
      const isEmptyUserAnswer = !serverUserAnswer || serverUserAnswer === NULL_ANSWER_MARKER;

      if (isEmptyUserAnswer) {
        const defaultUserAnswer = getDefaultUserAnswer(questionType,
          question.rightAnswers);

        return {
          ...state,
          currentQuestion: question,
          questionType,
          answers,
          isFileUpload: question?.uploadFile === "checked",
          userAnswer: defaultUserAnswer,
          fileAnswer: "",
          userComment: "",
          error: {
            hasError: false,
            message: "",
          },
          isCommentRequiredAndEmpty: false,
        };
      }

      const userAnswer = parseServerUserAnswer(
        questionType,
        serverUserAnswer,
        answers,
        question.rightAnswers,
      );

      const fileAnswer =
        questionType === TestQuestionFrontType.Open ?
          question.fileAnswer ?? "" :
          "";

      const userComment =
        question.userComment === NULL_ANSWER_MARKER ?
          "" :
          question.userComment ?? "";

      return {
        ...state,
        currentQuestion: question,
        questionType,
        answers,
        isFileUpload: question?.uploadFile === "checked",
        userAnswer,
        fileAnswer,
        userComment,
      };
    }

    case TestSolutionActionType.AnswerChanged: {
      const nextValue = typeof action.payload === "function" ?
        action.payload(state.userAnswer) :
        action.payload;

      return {
        ...state,
        userAnswer: nextValue,
      };
    }

    case TestSolutionActionType.FileChanged: {
      const nextValue = typeof action.payload === "function" ?
        action.payload(state.fileAnswer) :
        action.payload;

      return {
        ...state,
        fileAnswer: nextValue,
      };
    }

    case TestSolutionActionType.CommentChanged: {
      const nextValue = typeof action.payload === "function" ?
        action.payload(state.userComment) :
        action.payload;

      return {
        ...state,
        userComment: nextValue,
      };
    }

    case TestSolutionActionType.ValidationChanged: {
      const nextValue = typeof action.payload === "function" ?
        action.payload(state.error) :
        action.payload;

      return {
        ...state,
        error: nextValue,
      };
    }

    case TestSolutionActionType.CommentRequiredChanged: {
      return {
        ...state,
        isCommentRequiredAndEmpty: action.payload,
      };
    }

    case TestSolutionActionType.SaveLoadingChanged: {
      return {
        ...state,
        isSaveAnswerLoading: action.payload,
      };
    }

    case TestSolutionActionType.PrevQuestion: {
      const nextIndex = Math.max(0, state.questionCurrentIndex - 1);

      return {
        ...state,
        questionCurrentIndex: nextIndex,
        userAnswer: WAIT_USER_ANSWER_DATA,
        fileAnswer: "",
        userComment: "",
        error: {
          hasError: false,
          message: "",
        },
        isCommentRequiredAndEmpty: false,
      };
    }

    case TestSolutionActionType.NextQuestionApplied: {
      const isLastQuestion = action.payload.questionCount === state.questionCurrentIndex + 1;

      if (isLastQuestion) {
        return {
          ...state,
          checkComponentCover: true,
          questionCurrentIndex: 0,
          userAnswer: WAIT_USER_ANSWER_DATA,
          fileAnswer: "",
          userComment: "",
          error: {
            hasError: false,
            message: "",
          },
          isCommentRequiredAndEmpty: false,
        };
      }

      const maxQuestionIndex = Math.max(action.payload.questionCount - 1, 0);
      const nextIndex = action.payload.questionCount ?
        Math.min(state.questionCurrentIndex + 1, maxQuestionIndex) :
        state.questionCurrentIndex;

      return {
        ...state,
        questionCurrentIndex: nextIndex,
        userAnswer: WAIT_USER_ANSWER_DATA,
        fileAnswer: "",
        userComment: "",
        error: {
          hasError: false,
          message: "",
        },
        isCommentRequiredAndEmpty: false,
      };
    }

    case TestSolutionActionType.ContinueTest: {
      const unansweredQuestionIndex = action.payload.findIndex((question) =>
        (!question.answer || question.answer === NULL_ANSWER_MARKER) &&
        (!question.answers || question.answers === NULL_ANSWER_MARKER) &&
        (!question.userAnswer || question.userAnswer === NULL_ANSWER_MARKER) &&
        (!question.leftAnswers || question.leftAnswers === NULL_ANSWER_MARKER) &&
        (!question.rightAnswers || question.rightAnswers === NULL_ANSWER_MARKER));

      return {
        ...state,
        questionCurrentIndex: unansweredQuestionIndex === -1 ?
          0 :
          unansweredQuestionIndex,
        isTestInProgress: true,
      };
    }

    case TestSolutionActionType.StartTest: {
      return {
        ...state,
        isTestInProgress: true,
        isTestExpired: false,
        isTestFinished: false,
      };
    }

    case TestSolutionActionType.RestartTest: {
      return {
        ...state,
        isTestExpired: false,
        isTestInProgress: true,
        isTestFinished: false,
        checkComponentCover: false,
        questionCurrentIndex: 0,
        userAnswer: WAIT_USER_ANSWER_DATA,
        fileAnswer: "",
        userComment: "",
        isCommentRequiredAndEmpty: false,
        error: {
          hasError: false,
          message: "",
        },
      };
    }

    case TestSolutionActionType.SetQuestionIndex: {
      return {
        ...state,
        questionCurrentIndex: Math.max(0, action.payload),
      };
    }

    case TestSolutionActionType.ToggleCheckCover: {
      return {
        ...state,
        checkComponentCover: action.payload,
      };
    }

    case TestSolutionActionType.FinishTestSucceeded: {
      return {
        ...state,
        questionCurrentIndex: 0,
        isTestInProgress: false,
        isTestFinished: true,
        checkComponentCover: false,
      };
    }

    case TestSolutionActionType.FinishTestExpired: {
      return {
        ...state,
        isTestExpired: true,
        questionCurrentIndex: 0,
        isTestInProgress: false,
        isTestFinished: true,
        checkComponentCover: false,
      };
    }

    case TestSolutionActionType.TimerElapsed: {
      if (state.isTestExpired) return state;

      return {
        ...state,
        isTestExpired: true,
      };
    }

    case TestSolutionActionType.ResumeAfterTimeout: {
      return {
        ...state,
        isTestExpired: false,
      };
    }

    case TestSolutionActionType.TimerSynced: {
      const isSameRemainingTime =
        state.remainingTime === action.payload.remainingTime;
      const isSameTimeout =
        state.hasTestTimeout === action.payload.hasTestTimeout;
      const isSameReceivedAt =
        state.testDataReceivedAt === action.payload.receivedAt;

      if (isSameRemainingTime && isSameTimeout && isSameReceivedAt) {
        return state;
      }

      return {
        ...state,
        remainingTime: action.payload.remainingTime,
        hasTestTimeout: action.payload.hasTestTimeout,
        testDataReceivedAt: action.payload.receivedAt,
      };
    }

    default:
      return state;
  }
};

export const TestSolutionProvider: FC<Props> = ({
  children,
}) => {
  const [ state, dispatch ] = useReducer(reducer, INITIAL_TEST_SOLUTION_STATE);

  const saveAnswerRef = useRef<SaveAnswerHandler | null>(null);
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [ state ]);

  const timer = useTimer(
    state.remainingTime,
    state.hasTestTimeout,
    state.testDataReceivedAt,
  );

  const startTest = useCallback(() => {
    dispatch({
      type: TestSolutionActionType.StartTest,
    });
  }, []);

  const continueTest = useCallback((questions: ContinueQuestion[]) => {
    dispatch({
      type: TestSolutionActionType.ContinueTest,
      payload: questions,
    });
  }, []);

  const restartTest = useCallback(() => {
    dispatch({
      type: TestSolutionActionType.RestartTest,
    });
  }, []);

  const prevQuestion = useCallback(() => {
    dispatch({
      type: TestSolutionActionType.PrevQuestion,
    });
  }, []);

  const setQuestionIndex = useCallback((index: number) => {
    dispatch({
      type: TestSolutionActionType.SetQuestionIndex,
      payload: index,
    });
  }, []);

  const nextQuestionRequested = useCallback(async (questionCount: number) => {
    const current = stateRef.current;

    if (getComputedNextDisabled(current)) {
      return;
    }

    const {
      answers,
      questionType,
      userAnswer,
      currentQuestion,
      fileAnswer,
      userComment,
    } = current;

    if (!questionType) return;

    const formattedUserAnswer = getFormattedUserAnswer(
      questionType,
      userAnswer,
      answers,
      currentQuestion,
    );

    const isSendQuestionRequest = getIsSendRequest(
      questionType,
      formattedUserAnswer,
      currentQuestion?.userAnswer,
      userAnswer,
      currentQuestion?.userComment,
      userComment,
    );

    let saveSuccess = true;

    if (isSendQuestionRequest && saveAnswerRef.current) {
      try {
        const result: {
          answerValue: string,
          fileAnswer?: string,
          userComment?: string,
        } = {
          answerValue: formattedUserAnswer,
        };

        if (fileAnswer) result.fileAnswer = fileAnswer;

        if (userComment.trim() !== "") {
          result.userComment = userComment;
        }

        await saveAnswerRef.current(result);
      } catch (error) {
        console.error("Failed to save answer:", error);
        saveSuccess = false;
      }
    }

    if (!saveSuccess) return;

    dispatch({
      type: TestSolutionActionType.NextQuestionApplied,
      payload: {
        questionCount,
      },
    });
  }, []);

  const questionLoadedFromServer = useCallback((question?: TestQuestionData) => {
    dispatch({
      type: TestSolutionActionType.QuestionLoadedFromServer,
      payload: question,
    });
  }, []);

  const answerChanged = useCallback((value: SetStateAction<unknown>) => {
    dispatch({
      type: TestSolutionActionType.AnswerChanged,
      payload: value,
    });
  }, []);

  const fileChanged = useCallback((value: SetStateAction<string>) => {
    dispatch({
      type: TestSolutionActionType.FileChanged,
      payload: value,
    });
  }, []);

  const commentChanged = useCallback((value: SetStateAction<string>) => {
    dispatch({
      type: TestSolutionActionType.CommentChanged,
      payload: value,
    });
  }, []);

  const validationChanged = useCallback((value: SetStateAction<ValidationError>) => {
    dispatch({
      type: TestSolutionActionType.ValidationChanged,
      payload: value,
    });
  }, []);

  const commentRequiredChanged = useCallback((value: boolean) => {
    dispatch({
      type: TestSolutionActionType.CommentRequiredChanged,
      payload: value,
    });
  }, []);

  const saveLoadingChanged = useCallback((value: boolean) => {
    dispatch({
      type: TestSolutionActionType.SaveLoadingChanged,
      payload: value,
    });
  }, []);

  const toggleCheckCover = useCallback((value: boolean) => {
    dispatch({
      type: TestSolutionActionType.ToggleCheckCover,
      payload: value,
    });
  }, []);

  const finishTestSucceeded = useCallback(() => {
    dispatch({
      type: TestSolutionActionType.FinishTestSucceeded,
    });
  }, []);

  const finishTestExpired = useCallback(() => {
    dispatch({
      type: TestSolutionActionType.FinishTestExpired,
    });
  }, []);

  const timerSynced = useCallback((payload: {
    remainingTime: number | null,
    hasTestTimeout: boolean,
    receivedAt: number,
  }) => {
    dispatch({
      type: TestSolutionActionType.TimerSynced,
      payload,
    });
  }, []);

  const registerSaveAnswerHandler = useCallback((handler: SaveAnswerHandler | null) => {
    saveAnswerRef.current = handler;
  }, []);

  const resumeAfterTimeout = useCallback(() => {
    dispatch({
      type: TestSolutionActionType.ResumeAfterTimeout,
    });
  }, []);

  const memoValues = useMemo(() => ({
    questionType: state.questionType,
    currentQuestionIndex: state.questionCurrentIndex,
    answers: state.answers,
    userAnswer: state.userAnswer,
    isNextQuestionDisabled: getComputedNextDisabled(state),
    isFileUpload: state.isFileUpload,
    fileAnswer: state.fileAnswer,
    timer,
    error: state.error,
    currentQuestion: state.currentQuestion ?? DEFAULT_CURRENT_QUESTION,
    isCommentRequiredAndEmpty: state.isCommentRequiredAndEmpty,
    userComment: state.userComment,
    checkComponentCover: state.checkComponentCover,
    isTestInProgress: state.isTestInProgress,
    isTestExpired: state.isTestExpired,
    isTestFinished: state.isTestFinished,
  }), [ state, timer ]);

  const memoActions = useMemo(() => ({
    startTest,
    continueTest,
    restartTest,
    prevQuestion,
    nextQuestionRequested,
    questionLoadedFromServer,
    answerChanged,
    fileChanged,
    commentChanged,
    validationChanged,
    commentRequiredChanged,
    saveLoadingChanged,
    toggleCheckCover,
    setQuestionIndex,
    finishTestSucceeded,
    finishTestExpired,
    timerSynced,
    resumeAfterTimeout,
    registerSaveAnswerHandler,
    onClickPrevQuestion: prevQuestion,
    onClickNextQuestion: nextQuestionRequested,
    setUserAnswer: answerChanged as Dispatch<SetStateAction<unknown>>,
    setFileAnswer: fileChanged as Dispatch<SetStateAction<string>>,
    setError: validationChanged as Dispatch<SetStateAction<ValidationError>>,
    setIsCommentRequiredAndEmpty: commentRequiredChanged as Dispatch<SetStateAction<boolean>>,
    setUserComment: commentChanged as Dispatch<SetStateAction<string>>,
  }), [
    answerChanged,
    commentChanged,
    commentRequiredChanged,
    continueTest,
    fileChanged,
    finishTestExpired,
    finishTestSucceeded,
    nextQuestionRequested,
    prevQuestion,
    questionLoadedFromServer,
    registerSaveAnswerHandler,
    resumeAfterTimeout,
    restartTest,
    saveLoadingChanged,
    setQuestionIndex,
    startTest,
    timerSynced,
    toggleCheckCover,
    validationChanged,
  ]);

  /** экран "время истекло" при обнулении таймера — тест при этом не завершается */
  useEffect(() => {
    if (timer === 0) {
      dispatch({
        type: TestSolutionActionType.TimerElapsed,
      });
    }
  }, [ timer ]);

  return (
    <TestSolutionValuesContext.Provider value={memoValues}>
      <TestSolutionActionsContext.Provider value={memoActions}>
        {children}
      </TestSolutionActionsContext.Provider>
    </TestSolutionValuesContext.Provider>
  );
};

export const __test__ = {
  reducer,
  getComputedNextDisabled,
  NULL_ANSWER_MARKER,
  WAIT_USER_ANSWER_DATA,
  DEFAULT_RESULT_VALUE,
};
